import React, { useState, useEffect } from 'react';
import { Box, Tab } from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { useApiGet } from '../../../utils/httpClientTs';
import {
  getIRSFormSections,
  getIRSFormQuestions,
} from '../../../endpoints/irsForms';
import { IRSSection } from '../../../types/irsForms';
import IRSFormSection from './IRSFormSection';
import IRSFormSummary from './IRSFormSummary';
import { IRSFormDetail, IRSQuestion } from '../../../types/irsForms';

interface IRSFormSectionsProps {
  formId: string;
  formDetail: IRSFormDetail;
  editable: boolean;
  formType: 'lc' | 'sc' | 'rg';
  onApprove?: () => void;
  onReject?: () => void;
}

const IRSFormSections: React.FC<IRSFormSectionsProps> = ({
  formId,
  formDetail,
  editable,
  formType,
  onApprove,
  onReject,
}) => {
  const [value, setValue] = useState<string>('');
  const [sections, setSections] = useState<IRSSection[]>([]);
  const [allQuestions, setAllQuestions] = useState<IRSQuestion[]>([]);

  const { data: sectionsData, isLoading: sectionsLoading } = useApiGet(
    ['irs-form-sections', formId],
    () => getIRSFormSections(formId),
    {
      enabled: !!formId,
      retry: 1,
    }
  );

  const { data: allQuestionsData } = useApiGet(
    ['irs-form-all-questions', formId],
    () => getIRSFormQuestions(formId),
    {
      enabled: !!formId,
      retry: 1,
    }
  );

  useEffect(() => {
    if (allQuestionsData) {
      setAllQuestions(allQuestionsData);
    }
  }, [allQuestionsData]);

  useEffect(() => {
    if (sectionsData) {
      setSections(sectionsData);
      // Set initial tab to first section
      if (sectionsData.length > 0 && !value) {
        setValue(sectionsData[0].id);
      }
    }
  }, [sectionsData, value]);

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  if (sectionsLoading) {
    return <Box>Loading sections...</Box>;
  }

  return (
    <Box sx={{ width: '100%' }}>
      <TabContext value={value}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <TabList
            onChange={handleChange}
            aria-label="IRS form sections"
            TabIndicatorProps={{
              sx: { height: '6px' },
            }}
            variant="scrollable"
            scrollButtons="auto"
          >
            {sections.map((section) => (
              <Tab key={section.id} label={section.name} value={section.id} />
            ))}
            <Tab label="Summary" value="summary" />
          </TabList>
        </Box>
        {sections.map((section) => (
          <TabPanel key={section.id} value={section.id}>
            <IRSFormSection
              formId={formId}
              sectionId={section.id}
              editable={editable}
              formType={formType}
            />
          </TabPanel>
        ))}
        <TabPanel value="summary">
          <IRSFormSummary
            formDetail={formDetail}
            questions={allQuestions}
            editable={editable}
            onApprove={onApprove}
            onReject={onReject}
          />
        </TabPanel>
      </TabContext>
    </Box>
  );
};

export default IRSFormSections;
